// ─── Loading things, honestly ────────────────────────────────────────────────
//
// Three states, always distinguished: loading, failed, and loaded. It is easy
// to collapse the first two — `data === null` then means both
// "not yet" and "it broke" — and the screen then shows an empty diary either
// way. An empty diary is a claim: it says nobody is booked. Saying that
// because a request failed is precisely the confabulation this product exists
// not to commit.
//
// So `error` is its own state and every caller has to render it.

import { useCallback, useEffect, useRef, useState } from "react";

export type Async<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Re-run the loader — after a mutation, or after a failure. */
  reload: () => void;
};

export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Which request is current. A slow response for last week's dates must not
  // overwrite a fast one for this week's — the calendar would then be showing
  // a month the person had already navigated away from.
  const latest = useRef(0);

  useEffect(() => {
    const ticket = ++latest.current;
    let live = true;
    setLoading(true);
    load().then(
      result => {
        if (!live || ticket !== latest.current) return;
        setData(result);
        setError(null);
        setLoading(false);
      },
      (err: unknown) => {
        if (!live || ticket !== latest.current) return;
        // The data is deliberately LEFT as it was rather than cleared. Showing
        // yesterday's diary alongside "could not refresh" is more useful than
        // showing an empty one, as long as the failure is visible.
        setError(err instanceof Error ? err.message : "Something went wrong.");
        setLoading(false);
      },
    );
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce(n => n + 1), []);
  return { data, error, loading, reload };
}
